import type {createBusinessRuntime} from '../../../packages/runtime/business-runtime.ts';
import type {
  SmtReportingQueryResult,
} from '../../../packages/runtime/smt-reporting-contract.ts';
import type {TodayOperationsProjection} from '../../../packages/reporting/reporting-projection.ts';
import type {ResolvedDashboardComparisonProjection} from '../../../packages/reporting/dashboard-comparison.ts';

type BusinessRuntime = ReturnType<typeof createBusinessRuntime>;
type ReportingRuntime = Pick<BusinessRuntime, 'smtReporting'>;

export interface OwnerReportingPortConfig {
  readonly actorId: string;
  readonly businessDate: string;
  readonly now: () => string;
}

export interface OwnerDashboardSnapshot {
  readonly today: TodayOperationsProjection;
  readonly comparison: ResolvedDashboardComparisonProjection;
}

export interface OwnerReportingPort {
  readonly loadDashboard: () => Promise<OwnerDashboardSnapshot>;
}

const accepted = <K extends 'report.today' | 'report.dashboard-comparison'>(
  result: SmtReportingQueryResult,
  kind: K,
): Extract<SmtReportingQueryResult, {readonly kind: 'accepted'; readonly queryKind: K}> => {
  if (result.kind !== 'accepted' || result.queryKind !== kind) {
    const code = result.kind === 'rejected' ? result.code : 'OWNER_REPORTING_RESULT_MISMATCH';
    throw new Error(code);
  }
  return result as Extract<SmtReportingQueryResult, {readonly kind: 'accepted'; readonly queryKind: K}>;
};

export function createOwnerReportingPort(
  runtime: ReportingRuntime,
  config: OwnerReportingPortConfig,
): OwnerReportingPort {
  return {
    async loadDashboard() {
      const at = config.now();
      const context = {actorId: config.actorId, at};
      const [todayResult, comparisonResult] = await Promise.all([
        runtime.smtReporting.query({kind: 'report.today'}, context),
        runtime.smtReporting.query({
          kind: 'report.dashboard-comparison',
          currentBusinessDate: config.businessDate,
          currentCutoffAt: at,
        }, context),
      ]);
      return {
        today: accepted(todayResult, 'report.today').report,
        comparison: accepted(comparisonResult, 'report.dashboard-comparison').report,
      };
    },
  };
}
